import {
  ArrowTrendingUpIcon,
  BellAlertIcon,
  BookOpenIcon,
  CalendarIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  GiftIcon,
  IdentificationIcon,
  MegaphoneIcon,
  NewspaperIcon,
  PencilSquareIcon,
  ShoppingBagIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';

import { ClientOnly } from '@/components/ClientOnly';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Logo } from '@/components/Logo';

import UserDropdown from '@/components/Nav/UserDropdown';
import useMe from './utils/useMe';
import { usePageContext } from './utils/usePageContext';
import { useRasContext } from './utils/useRasContext';

import { CollapsibleNavGroup, NavGroupLabel } from '@/components/Nav/NavGroup';
import NavLink from '@/components/Nav/NavLink';
import type { Link } from '@/components/Nav/NavLink';

export default Nav;

// Truly standalone pages that carry their own marketing header/footer and never
// show the app sidebar for *anyone* (signed-in or not): the homepage, the auth
// screens, and a few dedicated marketing funnels.
//
// Do NOT add "in-app" content landings here. HIDE_ON removes the sidebar for
// signed-in users too, which strands them with no navigation (the /app
// regression, issue #1405). Those routes belong in MARKETING_LANDING_ROUTES.
const HIDE_ON = [
  '/',
  '/signin',
  '/register',
  '/forgot-password',
  '/links',
  '/mailing-list',
  '/who-is-ras-for',
  // Unlisted CFB Results promo archive (#1495): a self-contained microsite that
  // carries its own full-page chrome (it renders inside a full-viewport iframe),
  // so it must not have the app sidebar layered over it. Not an in-app landing —
  // it is standalone like the homepage. Guarded only by being unlinked, like /vsin.
  '/cfb-results',
];

// Public, shareable "in-app" content landings. Convention (header-vs-sidebar-by-
// auth): signed-out visitors get the marketing header — rendered by the page via
// <MarketingHeaderSlot /> — while signed-in users get the app sidebar below so
// they can navigate the app. Add a route here AND drop <MarketingHeaderSlot /> at
// the top of its +Page.tsx; never add it to HIDE_ON.
const MARKETING_LANDING_ROUTES = ['/order', '/app'];

// ── Grouped nav config ───────────────────────────────────────────────────────
// The sidebar is grouped (Plays / Account / Walls' Blog / Guides / Staff) for
// scannability. Every href and role-gate below is unchanged from the old flat
// list — this is a structural reorg only. `roleRequired` links are filtered per
// group in the render below.

// 1. Plays — the primary, full-weight group at the top.
const PLAYS_LINKS: Link[] = [
  {
    count: { dataPath: '/release-center', dataName: 'messageReceipt/created' },
    href: '/release-center',
    icon: MegaphoneIcon,
    text: 'Release Center',
  },
  {
    href: '/results',
    startsWithHref: '/results',
    icon: ArrowTrendingUpIcon,
    text: 'My Results',
    badge: 'pending',
    roleRequired: true,
  },
  {
    href: '/archive',
    startsWithHref: '/archive',
    icon: ChartBarSquareIcon,
    text: 'All Results',
  },
  {
    href: '/release-schedule',
    icon: CalendarIcon,
    text: 'Release Schedule',
  },
];

// 2. Account — Order + Support Chat. The "Active services" summary is folded in
// under this group in the render (no longer a floating standalone block).
const ACCOUNT_LINKS: Link[] = [
  {
    href: '/order',
    startsWithHref: '/order',
    icon: ShoppingBagIcon,
    text: 'Order',
  },
  {
    count: {
      dataPath: '/support-chat',
      dataName: 'directMessageReceipt/created',
      dataCb: (_event, json, setCount, map, me) => {
        if (me?.role === 'admin' || me?.role === 'staff') {
          // I'm an admin receiving a DM notification
          if (
            (json.data.user?.role === 'admin' || json.data.user?.role === 'staff') &&
            json.data.receiverUserId
          ) {
            // When an admin messages a user, we clear all the notifications for this chat for everyone

            const path = `/support-chat/${json.data.receiverUserId}`;
            const pathCount = map.current.get(path);

            if (pathCount) {
              setCount((count) => Math.max(count - pathCount, 0));
              map.current.delete(path);
            }
          } else {
            // When a regular user messages admins, we increment the count if we aren't on that page already
            const path = `/support-chat/${json.data.userId}`;
            const pathCount = map.current.get(path);

            // 1647 is Cleatus
            if (!pathCount && location.pathname !== path && path !== '/support-chat/1647') {
              map.current.set(path, 1);
              setCount((count) => count + 1);
            }
          }
        } else {
          // I'm a regular user receiving a DM
          const path = '/support-chat';
          const pathCount = map.current.get(path);

          if (json.data.userId === me?.id) {
            // If I got it from myself, set unread to 0
            map.current.delete(path);
            setCount(0);
          } else {
            // Otherwise
            map.current.set(path, (pathCount || 0) + 1);
            setCount((count) => count + 1);
          }
        }
      },
      dataNavCb(path, setCount, map) {
        const pathCount = map.current.get(path);

        if (pathCount) {
          setCount((count) => Math.max(count - pathCount, 0));
          map.current.delete(path);
        }
      },
    },
    href: '/support-chat',
    startsWithHref: '/support-chat',
    icon: ChatBubbleLeftRightIcon,
    text: 'Support Chat',
  } as Link<API.DirectMessageReceipt>,
];

// 3. Walls' Blog — standalone content link (NOT a guide), rendered full-weight
// on its own after Account.
const WALLS_LINK: Link = {
  href: '/walls',
  startsWithHref: '/walls',
  icon: NewspaperIcon,
  text: "Walls' Blog",
};

// 4. Guides — collapsed-by-default reference group.
const GUIDES_LINKS: Link[] = [
  {
    href: '/welcome-guide',
    icon: BookOpenIcon,
    text: 'Welcome Guide',
  },
  {
    href: '/alerts-guide',
    icon: BellAlertIcon,
    text: 'Alert Guide',
  },
  {
    href: '/about',
    icon: IdentificationIcon,
    text: 'About RAS',
  },
];

type TempLink = {
  href: string;
  text: string;
};

const TEMPLINKS: TempLink[] = import.meta.env.DEV
  ? [
      {
        href: '/signin',
        text: 'Sign in',
      },
      {
        href: '/register',
        text: 'Sign Up',
      },
      {
        href: '/forgot-password',
        text: 'Forgot Password',
      },
      {
        href: '/404',
        text: '404',
      },
      {
        href: '/',
        text: 'Index',
      },
    ]
  : [];

function Nav({ withoutUser = false }) {
  const { urlParsed, urlPathname } = usePageContext();
  const ref = useRef<HTMLInputElement>(null);

  const rasContext = useRasContext();
  // biome-ignore lint/correctness/useHookAtTopLevel: This is used in the <Suspense> of Nav, so it never changes
  const me = withoutUser ? null : useMe();
  const user = me || rasContext.data?.user;

  // Drop role-gated links (e.g. My Results) for signed-out visitors, then render
  // through NavLink. Applied per group below.
  const renderLinks = (links: Link[]) =>
    links
      .filter((link) => !(link.roleRequired && !user?.role))
      .map((link) => <NavLink key={link.href + link.text} link={link} urlParsed={urlParsed} />);

  const showStaff = me?.role === 'author' || me?.role === 'admin' || me?.role === 'staff';

  if (HIDE_ON.includes(urlPathname)) {
    return null;
  }

  // Marketing landing routes show the marketing header (not the sidebar) to
  // signed-out visitors, but signed-in users keep the app sidebar so they can
  // navigate out. Match the route exactly or as a path prefix (e.g. /order/123)
  // without over-matching siblings like /apply.
  const isMarketingLanding = MARKETING_LANDING_ROUTES.some(
    (route) => urlPathname === route || urlPathname.startsWith(`${route}/`),
  );

  if (isMarketingLanding && !user) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 z-20 -translate-x-full bg-zinc-400/20 backdrop-blur-xs transition-transform peer-checked/nav-checkbox:-translate-x-0 peer-focus-within/nav:-translate-x-0 dark:bg-black/40" />
      <input
        ref={ref}
        id="nav-checkbox"
        type="checkbox"
        className="peer/nav-checkbox absolute z-[-1] m-0 size-10 border-collapse cursor-pointer border-none border-transparent bg-transparent p-0 opacity-0 md:hidden"
        defaultChecked={false}
      />
      {/* Safe-area padding lives on the <aside> itself (not its content) so the
          dark bg fills the box INCLUDING the padding — on iOS the notch/home-
          indicator bands paint dark instead of showing white, while the content
          articles below sit clear of the notch and home indicator. Insets are 0
          on non-notch devices, so desktop/Android are unaffected (issue #1453). */}
      <aside
        id="ras-sidebar"
        aria-label="Sidebar"
        className="peer/nav fixed top-0 left-0 z-40 flex h-[100dvh] w-[100dvw] flex-none -translate-x-full flex-col bg-zinc-900 pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)] text-sm text-white transition-transform select-none peer-checked/nav-checkbox:translate-x-0 md:w-72 md:translate-x-0 dark:border-r dark:border-white/10 dark:bg-zinc-950"
        onClick={(event) => {
          const target = event.target as HTMLAnchorElement;

          if (target?.closest('a[href]') && ref.current) {
            // Immediately close menu if anchor clicked
            target.blur();
            ref.current.focus();
            ref.current.click();
          }
        }}
      >
        <article className="flex grow flex-col space-y-5 overflow-y-auto overscroll-contain p-4 px-6">
          <div className="flex items-center justify-between">
            <Logo className="size-10 select-none" />
            <div className="flex flex-row items-center">
              <a href="/settings">
                <Cog6ToothIcon
                  height={24}
                  className="hidden size-6 shrink-0 text-zinc-400 hover:text-white active:text-white md:block"
                  aria-hidden="true"
                />
              </a>
              <label
                htmlFor="nav-checkbox"
                className="ml-2 text-zinc-400 peer-checked/nav-checkbox:inline-block peer-focus-within/nav:inline-block hover:text-white md:pointer-events-none md:hidden md:opacity-0"
              >
                <XMarkIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
              </label>
            </div>
          </div>
          <ErrorBoundary>
            <ClientOnly component={() => import('@/components/Nav/AppPromoCard')} />
          </ErrorBoundary>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col space-y-6">
              {/* 1. Plays */}
              <li>
                <NavGroupLabel>Plays</NavGroupLabel>
                <ul role="list" className="-mx-2 mt-1 space-y-1">
                  {renderLinks(PLAYS_LINKS)}
                </ul>
              </li>

              {/* 2. Account — links + folded-in Active services summary */}
              <li>
                <NavGroupLabel>Account</NavGroupLabel>
                <ul role="list" className="-mx-2 mt-1 space-y-1">
                  {renderLinks(ACCOUNT_LINKS)}
                </ul>
                <ErrorBoundary>
                  <ClientOnly component={() => import('@/components/Nav/ActiveServices')} />
                </ErrorBoundary>
              </li>

              {/* 3. Walls' Blog — standalone content link (not a guide) */}
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  <NavLink link={WALLS_LINK} urlParsed={urlParsed} />
                </ul>
              </li>

              {/* 4. Guides — collapsed by default */}
              <li>
                <CollapsibleNavGroup
                  label="Guides"
                  links={GUIDES_LINKS}
                  urlParsed={urlParsed}
                  storageKey="ras-nav-guides"
                />
              </li>

              {/* 5. Staff — role-gated, below a divider */}
              {showStaff && (
                <li>
                  <hr className="mb-3 h-px border-0 bg-white/10" />
                  <NavGroupLabel>Staff</NavGroupLabel>
                  <ul role="list" className="-mx-2 mt-1 space-y-1">
                    {/* Editor entry — Walls' blog CMS, only for authors/admins */}
                    {(me?.role === 'author' || me?.role === 'admin') && (
                      <li>
                        <a
                          href="/editor"
                          data-active={urlParsed.pathnameOriginal.startsWith('/editor')}
                          className="flex w-full items-center gap-x-3 rounded px-3 py-2 leading-6 font-semibold text-emerald-300 transition-[background-color,color] duration-150 hover:bg-emerald-400/15 hover:text-emerald-200 active:bg-emerald-400/20 data-[active=true]:bg-emerald-400/20 data-[active=true]:text-emerald-200 data-[active=true]:ring-1 data-[active=true]:ring-emerald-400/30 data-[active=true]:ring-inset"
                        >
                          <PencilSquareIcon className="size-6 shrink-0" aria-hidden="true" />
                          <span className="flex-1 whitespace-nowrap">Editor</span>
                        </a>
                      </li>
                    )}

                    {/* Admin entry — only visible to admin/staff */}
                    {(me?.role === 'admin' || me?.role === 'staff') && (
                      <li>
                        <a
                          href="/admin"
                          data-active={urlParsed.pathnameOriginal.startsWith('/admin')}
                          className="flex w-full items-center gap-x-3 rounded px-3 py-2 leading-6 font-semibold text-amber-300 transition-[background-color,color] duration-150 hover:bg-amber-400/15 hover:text-amber-200 active:bg-amber-400/20 data-[active=true]:bg-amber-400/20 data-[active=true]:text-amber-200 data-[active=true]:ring-1 data-[active=true]:ring-amber-400/30 data-[active=true]:ring-inset"
                        >
                          <WrenchScrewdriverIcon className="size-6 shrink-0" aria-hidden="true" />
                          <span className="flex-1 whitespace-nowrap">Admin</span>
                        </a>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              {/* Temporary Links (dev only) */}
              {TEMPLINKS.length > 0 && (
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {TEMPLINKS.map((link) => {
                      return (
                        <li key={link.href + link.text}>
                          <a
                            href={link.href}
                            data-active={urlParsed.pathnameOriginal === link.href}
                            className="flex w-full items-center rounded-sm p-2 text-zinc-400 transition-[width,color,background-color] duration-500 ease-out hover:bg-white/5 hover:text-white hover:duration-75 active:bg-white/5 active:text-white data-[active=true]:bg-white/10 data-[active=true]:text-white"
                          >
                            <span className="ml-3 flex-1 whitespace-nowrap">{link.text}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              )}
            </ul>
          </nav>
        </article>
        <article className="mt-auto flex grow-0 flex-col space-y-1 p-2">
          <RotatingPromo />
          <hr className="h-px border-0 bg-white/10" />
          <div className="flex grow justify-between gap-x-1.5">
            <ErrorBoundary>
              <ClientOnly
                component={() => import('@/components/Nav/UserDropdown')}
                key="client-user"
              >
                <UserDropdown withoutUser={true} user={user} key="server-user" />
              </ClientOnly>
              <ClientOnly component={() => import('@/components/UI/DesktopNotifications')} />
            </ErrorBoundary>
          </div>
        </article>
      </aside>
    </>
  );
}

// ── Rotating sportsbook promo ────────────────────────────────────────────────

// The sportsbook offers live on our content site (sportsbookbonuses.com) rather
// than linking straight to the books — required for X-ads compliance (#1239) and
// the strategic funnel (#1268). External, so open in a new tab / the system
// browser on mobile instead of navigating away from the app.
const PROMOS: { href: string; text: string }[] = [
  {
    href: 'https://sportsbookbonuses.com/betonline',
    text: 'Get $250 credit with BetOnline',
  },
  {
    href: 'https://sportsbookbonuses.com/bet105',
    text: 'Get $250 credit with bet105',
  },
  {
    href: 'https://sportsbookbonuses.com/oddslogic',
    text: 'Get $250 credit with OddsLogic',
  },
];

const ROTATE_MS = 6000;

function RotatingPromo() {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % PROMOS.length);
        setVisible(true);
      }, 300); // matches the CSS transition duration
    }, ROTATE_MS);

    return () => clearInterval(interval);
  }, []);

  const promo = PROMOS[index];

  return (
    <a
      href={promo.href}
      target="_blank"
      rel="noreferrer sponsored noopener"
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 300ms ease',
      }}
      className="flex items-center gap-x-2 rounded-sm px-3 py-1.5 text-xs text-zinc-500 transition-colors hover:text-zinc-300"
    >
      <GiftIcon className="size-4 shrink-0" aria-hidden="true" />
      <span className="truncate">{promo.text}</span>
    </a>
  );
}
